/**
 * Thin wrapper around Google Analytics 4 (gtag.js). The measurement ID is
 * baked into the bundle at build time via `VITE_GA_MEASUREMENT_ID`. When it
 * is missing (e.g. local dev without GA configured), every function in this
 * module is a no-op so callers never need to null-check.
 *
 * Routes under `/theadmin` are excluded from tracking by design — analytics
 * should reflect what public visitors see, not admin activity.
 *
 * ## SPA navigation lifecycle
 *
 * On each route change the `AnalyticsRouteTracker` calls `startNavigation`,
 * which:
 *
 *   1. Sets `document.title` to the path-derived base title so the browser
 *      tab is correct immediately (even before any data has loaded).
 *   2. Buffers the GA `page_view` for `NAVIGATION_FLUSH_DELAY_MS` so that
 *      dynamic pages (event, decklist, subarchetype) have a chance to call
 *      `setNavigationTitle` with a richer, data-aware title before the
 *      event is sent. The first such call flushes the pending page_view
 *      with the rich title; otherwise the buffer flushes on its own with
 *      the base title. Exactly one `page_view` is sent per navigation.
 */

export const SITE_NAME = "Liga Legacy";

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as
  | string
  | undefined;

const ADMIN_PATH_PREFIX = "/theadmin";

/**
 * How long to wait after a route change before flushing the GA page_view
 * with whatever title is set. Short enough to feel instantaneous, long
 * enough for cached react-query results to resolve and publish their
 * specific titles.
 */
const NAVIGATION_FLUSH_DELAY_MS = 300;

let initialized = false;

interface PendingNavigation {
  path: string;
  baseTitle: string;
  resolvedTitle: string | null;
  flushTimer: ReturnType<typeof setTimeout>;
}

let pendingNavigation: PendingNavigation | null = null;

function isEnabled(): boolean {
  return (
    initialized &&
    typeof window !== "undefined" &&
    typeof window.gtag === "function"
  );
}

function isAdminPath(path: string): boolean {
  return path === ADMIN_PATH_PREFIX || path.startsWith(`${ADMIN_PATH_PREFIX}/`);
}

export function initAnalytics(): void {
  if (initialized) return;
  if (!MEASUREMENT_ID) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  // Must use `arguments`, not rest params — GA4 checks for Arguments objects in dataLayer.
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    cookie_domain: window.location.hostname,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    MEASUREMENT_ID,
  )}`;
  document.head.appendChild(script);

  initialized = true;
}

/**
 * Begin tracking a new SPA navigation. Updates `document.title` immediately
 * for browser tab UX and schedules a deferred GA page_view. Pages can call
 * `setNavigationTitle` during the buffer window to enrich the title sent
 * to GA.
 */
export function startNavigation(path: string, baseTitle: string): void {
  flushPendingNavigation();

  if (typeof document !== "undefined") {
    document.title = baseTitle;
  }

  pendingNavigation = {
    path,
    baseTitle,
    resolvedTitle: null,
    flushTimer: setTimeout(flushPendingNavigation, NAVIGATION_FLUSH_DELAY_MS),
  };
}

/**
 * Publish a resolved page title from a page component (typically once its
 * react-query data has loaded). Always updates `document.title` for the
 * browser tab. If the call lands within the navigation buffer window, the
 * buffered `page_view` is flushed to GA immediately with this title;
 * otherwise the rich title is reflected only in the browser tab, since
 * GA has already received the base title for this navigation.
 */
export function setNavigationTitle(title: string): void {
  if (typeof document !== "undefined") {
    document.title = title;
  }
  if (!pendingNavigation) return;
  pendingNavigation.resolvedTitle = title;
  flushPendingNavigation();
}

function flushPendingNavigation(): void {
  const nav = pendingNavigation;
  if (!nav) return;
  pendingNavigation = null;
  clearTimeout(nav.flushTimer);
  sendPageView(nav.path, nav.resolvedTitle ?? nav.baseTitle);
}

function sendPageView(path: string, title: string): void {
  if (!isEnabled()) return;
  if (isAdminPath(path)) return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title,
  });
}

/**
 * Low-level `page_view` send. Bypasses the navigation buffer — prefer
 * `startNavigation` + `setNavigationTitle` from SPA code.
 */
export function trackPageView(path: string, title?: string): void {
  const resolved =
    title ?? (typeof document !== "undefined" ? document.title : "");
  sendPageView(path, resolved);
}

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!isEnabled()) return;
  if (isAdminPath(window.location.pathname)) return;

  window.gtag("event", name, params ?? {});
}
